import React, { useState } from 'react';
import { toast } from 'sonner';

/**
 * First-login onboarding — blocking full-screen form that collects/confirms the
 * student's contact details so the portal can reach them (WhatsApp/Email/Push).
 * Mobile-first single column. Shown until portal.onboarding_completed is true.
 */
const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-700 mb-1">
      {label}{required && <span className="text-red-500"> *</span>}
    </label>
    {children}
    {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

const inputCls =
  'w-full text-sm rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#192650]/30 focus:border-[#192650]';

const StudentOnboarding = ({ studentApi, portal, onDone, editMode = false, onClose }) => {
  const [f, setF] = useState({
    email: portal?.email || '',
    whatsapp_number: portal?.whatsapp_number || '',
    phone: portal?.phone || '',
    parent_name: portal?.parent_name || '',
    parent_phone: portal?.parent_phone || '',
    address: portal?.address || '',
    city: portal?.city || '',
    state: portal?.state || '',
    country: portal?.country || 'India',
    pincode: portal?.pincode || '',
    date_of_birth: portal?.date_of_birth || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!f.email || !f.email.includes('@')) return toast.error('Please enter a valid email');
    if (!f.whatsapp_number.trim()) return toast.error('WhatsApp number (with country code) is required');
    if (!f.parent_name.trim()) return toast.error('Parent/guardian name is required');
    if (!f.parent_phone.trim()) return toast.error('Parent phone is required');
    if (!(f.address && f.city && f.state && f.country && f.pincode)) return toast.error('Please complete your full address');
    setSaving(true);
    try {
      await studentApi.post('/api/student-auth/my-portal/onboarding', f);
      toast.success(editMode ? 'Profile updated' : 'Details saved — welcome to your portal!');
      if (onDone) await onDone();
      if (editMode && onClose) onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-gray-50 overflow-y-auto" data-testid="student-onboarding">
      <div className="bg-[#192650] text-white px-4 py-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          {editMode && (
            <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="ob-close">
              <i className="fas fa-arrow-left text-lg"></i>
            </button>
          )}
          <div>
            <h1 className="text-base font-bold">{editMode ? 'My Profile' : "Welcome! Let's set up your profile"}</h1>
            <p className="text-[11px] text-white/70 mt-0.5">We use these to send you updates on documents, agreements & payments.</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        <Field label="Email" required>
          <input type="email" className={inputCls} value={f.email} onChange={set('email')} placeholder="you@example.com" data-testid="ob-email" />
        </Field>
        <Field label="WhatsApp Number (with country code)" required hint="Example: +91 98765 43210">
          <input type="tel" className={inputCls} value={f.whatsapp_number} onChange={set('whatsapp_number')} placeholder="+91 98765 43210" data-testid="ob-whatsapp" />
        </Field>
        <Field label="Personal Phone" hint="Optional — if different from WhatsApp">
          <input type="tel" className={inputCls} value={f.phone} onChange={set('phone')} placeholder="+91 ..." data-testid="ob-phone" />
        </Field>

        <div className="pt-1 border-t border-gray-200" />
        <Field label="Parent / Guardian Name" required>
          <input type="text" className={inputCls} value={f.parent_name} onChange={set('parent_name')} placeholder="Full name" data-testid="ob-parent-name" />
        </Field>
        <Field label="Parent / Guardian Phone (with code)" required>
          <input type="tel" className={inputCls} value={f.parent_phone} onChange={set('parent_phone')} placeholder="+91 ..." data-testid="ob-parent-phone" />
        </Field>

        <div className="pt-1 border-t border-gray-200" />
        <Field label="Address" required>
          <textarea rows={2} className={inputCls} value={f.address} onChange={set('address')} placeholder="House / street / area" data-testid="ob-address" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="City" required>
            <input type="text" className={inputCls} value={f.city} onChange={set('city')} data-testid="ob-city" />
          </Field>
          <Field label="State" required>
            <input type="text" className={inputCls} value={f.state} onChange={set('state')} data-testid="ob-state" />
          </Field>
          <Field label="Country" required>
            <input type="text" className={inputCls} value={f.country} onChange={set('country')} data-testid="ob-country" />
          </Field>
          <Field label="Pincode" required>
            <input type="text" className={inputCls} value={f.pincode} onChange={set('pincode')} data-testid="ob-pincode" />
          </Field>
        </div>
        <Field label="Date of Birth" hint="Optional">
          <input type="date" className={inputCls} value={f.date_of_birth} onChange={set('date_of_birth')} data-testid="ob-dob" />
        </Field>

        <button
          onClick={submit}
          disabled={saving}
          className="w-full bg-[#192650] text-white font-semibold py-3.5 rounded-xl text-sm mt-2 disabled:opacity-60 active:scale-[0.99] transition-transform"
          data-testid="ob-submit"
        >
          {saving ? 'Saving…' : (editMode ? 'Save Changes' : 'Save & Continue')}
        </button>
        <div className="h-6" />
      </div>
    </div>
  );
};

export default StudentOnboarding;
